import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

/* ---------- Auth Context ---------- */
import AuthProvider from './auth/AuthContext';
import PrivateRoute from './auth/PrivateRoute';
import PaidRoute from './auth/PaidRoute';
import ClientAuthProvider from './auth/ClientAuthContext';
import ClientPrivateRoute from './auth/ClientPrivateRoute';

/* ---------- Auth Pages ---------- */
import Signup from './pages/auth-pages/Signup';
import Login from './pages/auth-pages/Login';
import ForgotPassword from './pages/auth-pages/ForgotPassword';
import ResetPassword from './pages/auth-pages/ResetPassword';
import ConfirmSignup from './pages/auth-pages/ConfirmSignup';
import Plans from './pages/auth-pages/Plans';
import Demo from './pages/auth-pages/Demo';
import Checkout from './pages/auth-pages/Checkout';
import PublicPricing from './pages/PublicPricing';

/* ---------- Layout & Primary Pages ---------- */
import DashboardLayout from './layouts/DashboardLayout';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProductsServices from './pages/ProductsServices';
import TimeBillingPage from './pages/TimeBillingPage';
import Planner from './pages/Planner';
import Reports from './pages/Reports';
import NewProject from './pages/NewProject';

/* ---------- Project Detail & Sections ---------- */
import ProjectDetail from './pages/ProjectDetail';
import ProjectDetailsDefault from './pages/ProjectDetailsDefault';

import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import InvoicesSection from './pages/project-sections/InvoicesSection';
import CalendarSection from './pages/project-sections/CalendarSection';
import ToDoListSection from './pages/project-sections/ToDoListSection';
import PurchaseOrderDetail from './components/Projects/PurchaseOrders/PurchaseOrderDetail';
import ProposalsSection from './pages/project-sections/ProposalsSection';
import ProposalFormPage from './components/Projects/Proposals/ProposalFormPage';
import PublicProposalPage from './components/Projects/Proposals/PublicProposalPage';
import LibrarySection from './pages/project-sections/LibrarySection';
import FilesSection from './pages/project-sections/FilesSection';
import RetainersSection from './pages/project-sections/RetainersSection';
import CreditsSection from './pages/project-sections/CreditsSection';
import ScheduleSection from './pages/project-sections/ScheduleSection';
import ChatSection from './pages/project-sections/ChatSection';

import TimeBillingInvoiceViewPage from './pages/TimeBillingInvoiceViewPage';
import TimeBillingInvoiceFormPage from './pages/TimeBillingInvoiceFormPage';

import PublicInvoicePage from './components/Projects/Invoices/PublicInvoicePage';
import PublicTimeBillingInvoicePage from './components/Projects/Invoices/PublicTimeBillingInvoicePage';
import PublicPurchaseOrderPage from './components/Projects/PurchaseOrders/PublicPurchaseOrderPage';

/* ---------- Planner Components ---------- */
import Calendar from './components/Planner/Calendar';
import TodoList from './components/Planner/TodoList';
import Contacts from './components/Planner/Contacts';

/* ---------- Legal ---------- */
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';

/* ---------- Settings ---------- */
import SettingsPage from './pages/Settings';
import CompanySettings from './components/Settings/CompanySettings';
import BillingSettings from './components/Settings/BillingSettings';
import TeamSettings from './components/Settings/TeamSettings';
import TaxSettings from './components/Settings/TaxSettings';
import QuickbooksSettings from './components/Settings/QuickbooksSettings';
import StripeConnectSettings from './components/Settings/StripeConnectSettings';
import SalesAccountSettings from './components/Settings/SalesAccountSettings';
import TimeBillingSettings from './components/Settings/TimeBillingSettings';
import TagsSettings from './components/Settings/TagsSettings';
import TaggedItemsPage from './components/Settings/TaggedItemsPage';
import EmployeeDetail from './components/Settings/EmployeeDetail';
import EmailBrandingSettings from './components/Settings/EmailBrandingSettings';
import NotificationSettings from './components/Settings/NotificationSettings';
import ClientPortalSettings from './components/Settings/ClientPortalSettings';
import MessageTemplatesSettings from './components/Settings/MessageTemplatesSettings';
import InvoiceTemplateSettings from './components/Settings/InvoiceTemplateSettings';
import ProposalTemplateSettings from './components/Settings/ProposalTemplateSettings';

/* ---------- Client Portal ---------- */
import ClientPortalLayout from './layouts/ClientPortalLayout';
import ClientLogin from './pages/ClientPortal/ClientLogin';
import ClientAcceptInvite from './pages/ClientPortal/ClientAcceptInvite';
import ClientDashboard from './pages/ClientPortal/ClientDashboard';
import ClientProjectView from './pages/ClientPortal/ClientProjectView';
import ClientInvoiceDetail from './pages/ClientPortal/ClientInvoiceDetail';
import ClientProposalDetail from './pages/ClientPortal/ClientProposalDetail';
import ClientTimeBillingInvoiceDetail from './pages/ClientPortal/ClientTimeBillingInvoiceDetail';
import ClientPurchaseOrderDetail from './pages/ClientPortal/ClientPurchaseOrderDetail';
import ClientRetainerDetail from './pages/ClientPortal/ClientRetainerDetail';
import ClientItemStatusDetail from './pages/ClientPortal/ClientItemStatusDetail';
import ClientFaq from './pages/ClientPortal/ClientFaq';

import SupportPage from './pages/SupportPage';
import SupportArticlePage from './pages/SupportArticlePage';

/* ---------- Retainers ---------- */
import RetainerDetail from './components/Projects/Retainer/RetainerDetail';

import ProcurementTrackerPage from './components/Reports/ProcurementTrackerPage';
import ProfitByProjectDetail from './components/Reports/ProfitByProjectDetail';
import ProfitByProjectPage from './components/Reports/ProfitByProjectPage';

import PurchaseOrderFormPage from './components/Projects/PurchaseOrders/PurchaseOrderFormPage';
import TimeBillingReportPage from './components/Reports/TimeBillingReportPage';
import TimeBillingInvoiceReportPage from './components/Reports/TimeBillingInvoiceReportPage';
import TaskCompletionReportPage from './components/Reports/TaskCompletionReport';
import SalesTaxReportPage from './components/Reports/SalesTaxReportPage';
import ClientPaymentReportPage from './components/Reports/ClientPaymentReportPage';
import VendorPaymentReportPage from './components/Reports/VendorPaymentReportPage';
import ARAgingReportPage from './components/Reports/ARAgingReportPage';
import APAgingReportPage from './components/Reports/APAgingReportPage';
import RoomByRoomReportPage from './components/Reports/RoomByRoomReportPage';
import ItemTrackerReportPage from './components/Reports/ItemTrackerReportPage';
import InvoiceFormPage from './components/Projects/Invoices/InvoiceFormPage';
import NotificationCenter from './components/common/NotificationCenter';
import installAuthFetch from './utils/installAuthFetch';

function RootEntry() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  if (params.get('publicProposal')) {
    return <PublicProposalPage />;
  }

  if (params.get('publicInvoice')) {
    return <PublicInvoicePage />;
  }

  if (params.get('publicPOVendor')) {
    return <PublicPurchaseOrderPage variant="vendor" />;
  }

  if (params.get('publicPOClient')) {
    return <PublicPurchaseOrderPage variant="client" />;
  }

  return <Login />;
}

/* ---------- Stripe Initialization ---------- */
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
    'pk_live_51SSOO4I78YyRcYrYQonKIJgwXDYlwl0zTdHoq4ZnVKJ6SGvMd9qLmyC0mAOpSIhcgKtpe7Cc5DaVh8Ydba49Hdnw00siYIum3j'
);

installAuthFetch();

/* -------------------------------------------------------------------------- */
/* 🧩 Main Application                                                       */
/* -------------------------------------------------------------------------- */
function App() {
  return (
    <AuthProvider>
      <ClientAuthProvider>
      <Elements stripe={stripePromise}>
        <Router>
          <Routes>
            {/* ======================================================
               AUTH / PUBLIC (NO DASHBOARD LAYOUT)
            ====================================================== */}
            <Route path="/" element={<RootEntry />} />
            <Route path="signup" element={<Signup />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="confirm" element={<ConfirmSignup />} />
            <Route path="plans" element={<Plans />} />
            <Route path="pricing" element={<PublicPricing />} />
            <Route path="demo" element={<Demo />} />
            <Route path="checkout" element={<Checkout />} />

            {/* Public proposal / invoice (true public, scrollable) */}
            <Route path="public/proposal" element={<PublicProposalPage />} />
            <Route path="public/proposal/:shareToken" element={<PublicProposalPage />} />
            <Route path="public/invoice" element={<PublicInvoicePage />} />
            <Route path="public/invoice/:shareToken" element={<PublicInvoicePage />} />
            <Route path="public/timebilling-invoice/:shareToken" element={<PublicTimeBillingInvoicePage />} />
            <Route path="public/purchase-order/vendor" element={<PublicPurchaseOrderPage variant="vendor" />} />
            <Route path="public/purchase-order/vendor/:shareToken" element={<PublicPurchaseOrderPage variant="vendor" />} />
            <Route path="public/purchase-order/client" element={<PublicPurchaseOrderPage variant="client" />} />
            <Route path="public/purchase-order/client/:shareToken" element={<PublicPurchaseOrderPage variant="client" />} />

            {/* Legal */}
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="terms" element={<TermsOfService />} />

            {/* ======================================================
               CLIENT PORTAL (separate auth, separate layout)
            ====================================================== */}
            <Route path="client/login" element={<ClientLogin />} />
            <Route path="client/accept-invite/:clientId" element={<ClientAcceptInvite />} />
            <Route
              path="client"
              element={
                <ClientPrivateRoute>
                  <ClientPortalLayout />
                </ClientPrivateRoute>
              }
            >
              <Route path="dashboard" element={<ClientDashboard />} />
              <Route path="projects/:projectId" element={<ClientProjectView />} />
              <Route path="projects/:projectId/invoices/:invoiceId" element={<ClientInvoiceDetail />} />
              <Route path="projects/:projectId/proposals/:proposalId" element={<ClientProposalDetail />} />
              <Route path="projects/:projectId/timebilling-invoices/:tbInvoiceId" element={<ClientTimeBillingInvoiceDetail />} />
              <Route path="projects/:projectId/purchase-orders/:poId" element={<ClientPurchaseOrderDetail />} />
              <Route path="projects/:projectId/retainers/:retainerId" element={<ClientRetainerDetail />} />
              <Route path="projects/:projectId/items/:itemId" element={<ClientItemStatusDetail />} />
              <Route path="faq" element={<ClientFaq />} />
            </Route>

            {/* ======================================================
               PRIVATE DASHBOARD (SCOPED LAYOUT)
            ====================================================== */}
            <Route
              path="/*"
              element={
                <PrivateRoute>
                  <DashboardLayout />
                </PrivateRoute>
              }
            >
              {/* Dashboard */}
              <Route path="dashboard" element={<Dashboard />} />

              {/* Settings */}
              <Route path="settings" element={<SettingsPage />}>
                <Route path="company" element={<CompanySettings />} />
                <Route path="billing" element={<BillingSettings />} />
                <Route path="team" element={<TeamSettings />} />
                <Route path="team/:memberIndex" element={<EmployeeDetail />} />
                <Route path="tax" element={<TaxSettings />} />
                <Route path="time-billing" element={<TimeBillingSettings />} />
                <Route path="quickbooks" element={<QuickbooksSettings />} />
                <Route path="payments" element={<StripeConnectSettings />} />
                <Route path="sales-account" element={<SalesAccountSettings />} />
                <Route path="invoice-template" element={<InvoiceTemplateSettings />} />
                <Route path="proposal-template" element={<ProposalTemplateSettings />} />
                <Route path="tags" element={<TagsSettings />} />
                <Route path="tags/:tagName" element={<TaggedItemsPage />} />
                <Route path="email-branding" element={<EmailBrandingSettings />} />
                <Route path="notifications" element={<NotificationSettings />} />
                <Route path="client-portal" element={<ClientPortalSettings />} />
                <Route path="message-templates" element={<MessageTemplatesSettings />} />
              </Route>

              <Route path="support" element={<SupportPage />} />
              <Route path="support/:articleSlug" element={<SupportArticlePage />} />

              {/* Projects */}
              <Route path="projects/:projectId" element={<ProjectDetail />}>
                <Route index element={<ProjectDetailsDefault />} />
                <Route path="edit" element={<NewProject />} />

                <Route path="library" element={<LibrarySection />} />
                <Route path="files" element={<FilesSection />} />

                {/* Invoices */}
                <Route path="invoices" element={<InvoicesSection />} />
                <Route path="timebilling-invoices/new" element={<TimeBillingInvoiceFormPage />} />
                <Route
                  path="timebilling-invoices/:tbInvoiceId"
                  element={<TimeBillingInvoiceViewPage />}
                />
                <Route path="invoices/new" element={<InvoiceFormPage />} />
                <Route path="invoices/:invoiceId" element={<InvoiceFormPage />} />

                {/* Proposals */}
                <Route path="proposals" element={<ProposalsSection />} />
                <Route path="proposals/new" element={<ProposalFormPage />} />
                <Route path="proposals/:proposalId" element={<ProposalFormPage />} />

                {/* Purchase Orders */}
                <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
                <Route path="purchase-orders/new" element={<PurchaseOrderFormPage />} />
                <Route path="purchase-orders/:poId" element={<PurchaseOrderFormPage />} />

                {/* Retainers */}
                <Route path="retainers" element={<RetainersSection />} />
                <Route path="retainers/:retainerId" element={<RetainerDetail />} />

                {/* Credits */}
                <Route path="credits" element={<CreditsSection />} />

                {/* Schedule */}
                <Route path="schedule" element={<ScheduleSection />} />

                {/* Planner */}
                <Route path="calendar" element={<CalendarSection />} />
                <Route path="todo" element={<ToDoListSection />} />

                {/* Chat */}
                <Route path="chat" element={<ChatSection />} />
              </Route>

              {/* Products & Services */}
              <Route path="products" element={<ProductsServices />} />

              {/* Vendors (Vendor contacts from Planner > Contacts) */}
              <Route path="vendors" element={<Contacts vendorsOnly />} />

              {/* Time Billing */}
              <Route path="timebilling">
                <Route index element={<TimeBillingPage />} />
                <Route path=":projectId" element={<TimeBillingPage />} />
              </Route>

              {/* Planner */}
              <Route path="planner" element={<Planner />}>
                <Route index element={<div style={{ padding: 20 }}>Pick a Planner option</div>} />
                <Route path="calendar" element={<Calendar />} />
                <Route path="todo" element={<TodoList />} />
                <Route path="contacts" element={<Contacts />} />
              </Route>

              {/* Reports */}
              <Route path="reports" element={<Reports />}>
                <Route path="procurement-tracker" element={<ProcurementTrackerPage />} />
                <Route path="profit-detail" element={<ProfitByProjectDetail />} />
                <Route path="profit-by-project" element={<ProfitByProjectPage />} />
                <Route path="client-payment" element={<ClientPaymentReportPage />} />
                <Route path="vendor-payment" element={<VendorPaymentReportPage />} />
                <Route path="ar-aging" element={<ARAgingReportPage />} />
                <Route path="ap-aging" element={<APAgingReportPage />} />
                <Route path="room-by-room" element={<RoomByRoomReportPage />} />
                <Route path="time-billing-report" element={<TimeBillingReportPage />} />
                <Route path="tb-invoice-report" element={<TimeBillingInvoiceReportPage />} />
                <Route path="task-report" element={<TaskCompletionReportPage />} />
                <Route path="sales-tax-report" element={<SalesTaxReportPage />} />
                <Route path="item-tracker" element={<ItemTrackerReportPage />} />
              </Route>

              {/* New Project */}
              <Route path="new-project" element={<NewProject />} />
            </Route>
          </Routes>
        </Router>
        <NotificationCenter />
      </Elements>
      </ClientAuthProvider>
    </AuthProvider>
  );
}

export default App;
